import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { MediasZipResponse } from 'src/dto/dam/media-dto';
import { SpinnerService } from './spinner.service';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  constructor(
    private translate: TranslateService,
    private spinnerService: SpinnerService
  ) {}

  //Messaggio
  private messageSubject = new BehaviorSubject<string>('');
  private informationSubject = new BehaviorSubject<MediasZipResponse>({});

  //Operazioni
  private isErrorSubject = new BehaviorSubject<boolean>(false);
  private isSuccessSubject = new BehaviorSubject<boolean>(false);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  private isShowButtonConfirm = new BehaviorSubject<boolean>(false);

  //Modals
  private isVisibleSubject = new BehaviorSubject<boolean>(false);
  private isConfirmationSubject = new BehaviorSubject<boolean>(false);
  private isConfirmationCustomSubject = new BehaviorSubject<boolean>(false);
  private isErrorCustomSubject = new BehaviorSubject<boolean>(false);

  resolveFunction: ((value?: void | PromiseLike<void>) => void) | null = null;
  rejectFunction: ((reason?: any) => void) | null = null;

  get isSuccess$() {
    return this.isSuccessSubject.asObservable();
  }

  get isLoading$() {
    return this.isLoadingSubject.asObservable();
  }

  get isError$() {
    return this.isErrorSubject.asObservable();
  }

  get isVisible$() {
    return this.isVisibleSubject.asObservable();
  }

  get isConfirmation$() {
    return this.isConfirmationSubject.asObservable();
  }

  get isConfirmationCustom$() {
    return this.isConfirmationCustomSubject.asObservable();
  }

  get isErrorCustom$() {
    return this.isErrorCustomSubject.asObservable();
  }

  get message$() {
    return this.messageSubject.asObservable();
  }

  get information$() {
    return this.informationSubject.asObservable();
  }

  get showButton$() {
    return this.isShowButtonConfirm.asObservable();
  }

  //Avvia il modal (confirmation)
  confirm(messageKey: string, suffix?: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const translationKey = `ModalMessages.${messageKey}`;
      this.translate.get(translationKey).subscribe((message) => {
        let finalMessage = message || 'Messaggio di default';

        if (suffix) {
          finalMessage += ' ' + suffix;
        }

        this.messageSubject.next(finalMessage);
        this.isConfirmationSubject.next(true);
      });
      this.resolveFunction = resolve;
      this.rejectFunction = reject;
    });
  }

  errorCustom(
    messageKey: string,
    customMessage: string,
    withHeader?: boolean,
    params?: Record<string, any>
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const translationKey = `ModalMessages.${messageKey}`;

      if (withHeader) {
        if (params) {
          // Se ci sono params, usa la traduzione con i parametri
          this.translate.get(translationKey, params).subscribe((message) => {
            const content = customMessage || message;
            this.messageSubject.next(content);
          });
        } else {
          // Se non ci sono params, usa solo customMessage o messaggio di default
          const content = customMessage || 'Messaggio di default';
          this.messageSubject.next(content);
        }
      } else {
        this.translate
          .get(translationKey, params || {})
          .subscribe((message) => {
            const content =
              '<b>' +
              message +
              '</b><br>' +
              (customMessage || 'Messaggio di default');
            this.messageSubject.next(content);
          });
      }

      this.isErrorCustomSubject.next(true);
      this.resolveFunction = resolve;
      this.rejectFunction = reject;
    });
  }

  confirmWithCustomMessage(
    messageKey: string,
    customMessage: string,
    information?: MediasZipResponse
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const translationKey = `ModalMessages.${messageKey}`;
      this.translate.get(translationKey).subscribe((message) => {
        if (information) {
          this.informationSubject.next(information);
        }
        this.messageSubject.next(
          message + '<br>' + customMessage || 'Messaggio di default'
        );
        this.isVisibleSubject.next(false);
        this.isConfirmationCustomSubject.next(true);
        this.isShowButtonConfirm.next(true);
      });
      this.resolveFunction = resolve;
      this.rejectFunction = reject;
    });
  }

  //Avvia il modal (loading)
  open(messageKey: string): void {
    const translationKey = `ModalMessages.${messageKey}`;
    this.translate.get(translationKey).subscribe((message) => {
      this.messageSubject.next(message || 'Messaggio di default');
      this.isVisibleSubject.next(true);
      this.isConfirmationSubject.next(false);
      this.isLoadingSubject.next(true);
      this.spinnerService.hide();
    });
  }

  hide() {
    this.isVisibleSubject.next(false);
    this.isConfirmationSubject.next(false);
    this.isConfirmationCustomSubject.next(false);
    this.isSuccessSubject.next(false);
    this.isErrorSubject.next(false);
    this.isLoadingSubject.next(false);
    this.isShowButtonConfirm.next(false);
    this.isErrorCustomSubject.next(false);
  }

  executeReject() {
    if (this.rejectFunction) {
      this.rejectFunction();
      this.rejectFunction = null;
      // rejectFunction();
    }
  }

  executeConfirm() {
    if (this.resolveFunction) {
      this.resolveFunction();
      this.resolveFunction = null;
    }
  }

  // setMessage(messageKey: string): void {
  //   const translationKey = `confirmationMessages.${messageKey}`;
  //   this.translate.get(translationKey).subscribe((message) => {
  //     this.messageSubject.next(message || 'Messaggio di default');
  //   })
  // }

  isSuccess(success: boolean, messageKey: string): void {
    const translationKey = `ModalMessages.${messageKey}`;
    this.translate.get(translationKey).subscribe((message) => {
      this.messageSubject.next(message || 'Messaggio di default');
    });
    this.isLoadingSubject.next(false);
    this.isSuccessSubject.next(success);
  }

  isSuccessFromBackend(success: boolean, message: string): void {
    const translationKey = message;
    this.translate.get(translationKey).subscribe((message) => {
      this.messageSubject.next(message || 'Messaggio di default');
    });
    this.isLoadingSubject.next(false);
    this.isSuccessSubject.next(success);
  }

  isLoading(success: boolean): void {
    this.isLoadingSubject.next(success);
  }

  isError(success: boolean, messageKey: string): void {
    const translationKey = `ModalMessages.${messageKey}`;
    this.translate.get(translationKey).subscribe((message) => {
      this.messageSubject.next(message || 'Messaggio di default');
    });
    this.isLoadingSubject.next(false);
    this.isErrorSubject.next(success);
  }
}
